import { currencyFormat, numberFormat } from 'App/babel/components/Filters/Currencies';
import { Flex, Icon, Text, Tooltip } from '@upsales/components';
import { useTranslation } from 'Components/Helpers/translate';
import React, { useMemo } from 'react';
import { createListUrl } from 'App/helpers/filterHelpers';
import BemClass from '@upsales/components/Utils/bemClass';
import { Order } from 'App/resources/Model/Order';
import Suggestion from './Suggestion/Suggestion';
import { SearchAction } from 'Resources/AIChat';
import Client from 'App/resources/Model/Client';
import { ChatAction } from 'Resources/AIChat';
import UserStack from '../UserStack';

const CompanyComponent = ({
	client,
	hasRevenueFilter,
	hasProfitFilter,
	hasNoEmployeesFilter,
	onMinimizeChat
}: {
	client: Client;
	hasRevenueFilter: boolean;
	hasProfitFilter: boolean;
	hasNoEmployeesFilter: boolean;
	onMinimizeChat?: () => void;
}) => {
	const missingAllFilters = !hasRevenueFilter && !hasProfitFilter && !hasNoEmployeesFilter;
	const classes = new BemClass('AIChat');
	const { t } = useTranslation();
	const goToAccount = (id: number) => {
		onMinimizeChat?.();
		Tools.$state.go('account.dashboard', { id });
	};

	return (
		<Flex
			gap="u2"
			spacing="pl4 pr4 pb2 pt2"
			justifyContent="space-between"
			className={classes.elem('searchResult').b()}
			onClick={() => goToAccount(client.id)}
		>
			<Flex direction="column" gap="u1">
				<Text bold size="sm">
					{client.name}
				</Text>
				<Flex gap="u3">
					{hasRevenueFilter || missingAllFilters ? (
						<Tooltip title={t('default.turnover')}>
							<Flex gap="u1">
								<Icon name="bar-chart" color="grey-11" spacing="mr1" />
								<Text size="xs" color="grey-11">
									{currencyFormat(client.turnover, client.currency)}
								</Text>
							</Flex>
						</Tooltip>
					) : null}
					{hasProfitFilter || missingAllFilters ? (
						<Tooltip title={t('default.profit')}>
							<Flex gap="u1">
								<Icon name="chart-line" color="grey-11" spacing="mr1" />
								<Text size="xs" color="grey-11">
									{currencyFormat(client.profit, client.currency)}
								</Text>
							</Flex>
						</Tooltip>
					) : null}
					{hasNoEmployeesFilter || missingAllFilters ? (
						<Tooltip title={t('default.noEmployees')}>
							<Flex gap="u1">
								<Icon name="users" color="grey-11" spacing="mr1" />
								<Text size="xs" color="grey-11">
									{numberFormat(client.noEmployees)}
								</Text>
							</Flex>
						</Tooltip>
					) : null}
				</Flex>
			</Flex>

			<UserStack users={client.users} />
		</Flex>
	);
};

const OrderComponent = ({ order, onMinimizeChat }: { order: Order; onMinimizeChat?: () => void }) => {
	const classes = new BemClass('AIChat');
	const { t } = useTranslation();
	const goToOrder = (id: number) => {
		Tools.$upModal.open('editOrder', { id });
		onMinimizeChat?.();
	};

	return (
		<Flex
			gap="u2"
			spacing="pl4 pr4 pb2 pt2"
			justifyContent="space-between"
			className={classes.elem('searchResult').b()}
			onClick={() => goToOrder(order.id)}
		>
			<Flex direction="column" gap="u1">
				<Text bold size="sm">
					{order.description}
				</Text>
				<Tooltip title={t('default.value')}>
					<Flex gap="u1">
						<Icon name="dollar" color="grey-11" spacing="mr1" />
						<Text size="xs" color="grey-11">
							{currencyFormat(order.value, order.currency)}
						</Text>
					</Flex>
				</Tooltip>
			</Flex>

			<UserStack users={[order.user]} />
		</Flex>
	);
};

const SearchResultComponent = ({
	items,
	filters,
	entity,
	onMinimizeChat
}: {
	items: Client[] | Order[];
	filters?: SearchAction['filters'];
	entity: 'Client' | 'Order';
	onMinimizeChat?: () => void;
}) => {
	const { t } = useTranslation();

	const hasRevenueFilter = !!filters?.some(filter => filter.a === 'turnover');
	const hasProfitFilter = !!filters?.some(filter => filter.a === 'profit');
	const hasNoEmployeesFilter = !!filters?.some(filter => filter.a === 'noEmployees');
	const listUrl = useMemo(() => createListUrl(entity, filters ?? []), [filters, entity]);

	const goToList = (listUrl: string) => {
		onMinimizeChat?.();
		window.open(listUrl, '_self');
	};

	return (
		<Flex direction="column" gap="u2" spacing="pr4">
			{(items?.length ?? 0) > 0 ? (
				items.map(item =>
					entity === 'Client' ? (
						<CompanyComponent
							key={item.id}
							client={item as Client}
							hasRevenueFilter={hasRevenueFilter}
							hasProfitFilter={hasProfitFilter}
							hasNoEmployeesFilter={hasNoEmployeesFilter}
							onMinimizeChat={onMinimizeChat}
						/>
					) : (
						<OrderComponent key={item.id} order={item as Order} onMinimizeChat={onMinimizeChat} />
					)
				)
			) : (
				<Text color="grey-11" italic>
					{t('default.noResults')}
				</Text>
			)}
			{listUrl ? (
				<Suggestion
					icon="list"
					fullWidth={false}
					title={t('default.goToList')}
					onClick={() => goToList(listUrl)}
				/>
			) : null}
		</Flex>
	);
};

type Props = {
	action?: ChatAction;
	onMinimizeChat?: () => void;
};

export const ActionComponent = ({ action, onMinimizeChat }: Props) => {
	if (!action) {
		return null;
	}

	switch (action.type) {
		case 'search': {
			const { data, filters, entity } = action;
			return (
				<SearchResultComponent items={data} filters={filters} entity={entity} onMinimizeChat={onMinimizeChat} />
			);
		}
		default:
			return null;
	}
};
